/**
 * AdminCurriculumBuilder
 *
 * Admin-side curriculum editor. Replaces the learner-facing
 * `LessonListSidebar` on the Curriculum tab of the course editor.
 *
 * Design notes:
 *  - Each MODULE renders as a Card (clear container) with:
 *      • Drag handle + folder icon
 *      • Title + lesson count
 *      • Edit / Delete actions (visible on hover)
 *      • Nested list of lessons indented inside the card
 *      • Per-module "+ Add lesson" inline at the bottom
 *  - Each LESSON renders as a row with:
 *      • Drag handle
 *      • Type-specific icon (Video, Quiz, Document, Text, etc.)
 *      • Title
 *      • Type chip
 *      • Edit / Delete actions
 *  - Drag-and-drop:
 *      • Modules reorder among modules
 *      • Lessons reorder among lessons of the SAME module (cross-module
 *        moves are deferred — backend signature is per-course flat order;
 *        moving a lesson between modules would need a separate API)
 */

import React, { useMemo } from 'react';
import { Box, Button, Card, Chip, IconButton, Tooltip, Typography } from '../ui';
import {
  AddIcon,
  DeleteIcon,
  DragIndicatorIcon,
  EditIcon,
  LockIcon,
  LockOpenIcon,
} from '../icons';
import { lessonTypeMeta } from './contentTypeMeta';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Pastel sky-blue hover for lesson rows. Action.hover is too dark / grey
// for this dense list — this is much softer and reads as "this is
// interactive" without shouting.
const PASTEL_HOVER = '#f0f9ff';

export interface AdminCurriculumBuilderProps {
  modules: any[];
  lessons: any[];
  /** Currently-selected lesson, driving the right-side preview pane. */
  selectedLessonId?: number | null;
  /** Single-click on a lesson row — selects (shows in preview). */
  onSelectLesson?: (lesson: any) => void;
  onAddModule: () => void;
  onEditModule: (module: any) => void;
  onDeleteModule: (moduleId: number) => void;
  /** Per-module add — pre-fills the module dropdown in the dialog. */
  onAddLessonForModule: (moduleId: number) => void;
  /** Pencil icon — opens the full lesson editor modal. */
  onEditLesson: (lesson: any) => void;
  onDeleteLesson: (lessonId: number) => void;
  /** Lock toggle on a lesson row — flips the lesson's gate (Lesson.isGated). */
  onToggleGate: (lesson: any) => void;
  onReorderModules: (moduleIds: number[]) => void;
  onReorderLessons: (lessonIds: number[]) => void;
}


// ── Lesson row ───────────────────────────────────────────────────────────
function SortableLessonRow({
  lesson,
  selected,
  onSelect,
  onEdit,
  onDelete,
  onToggleGate,
}: {
  lesson: any;
  selected: boolean;
  onSelect: (l: any) => void;
  onEdit: (l: any) => void;
  onDelete: (id: number) => void;
  onToggleGate: (l: any) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `lesson-${lesson.id}`,
  });
  const meta = lessonTypeMeta(lesson.lessonType);

  // Gate state lives on the lesson (Lesson.isGated); the parent persists the
  // flip via a read-modify-write update of the lesson.
  const gated = lesson.isGated === true;
  const toggleGate = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleGate(lesson);
  };

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1,
        px: 1.5,
        borderRadius: 1,
        cursor: 'pointer',
        // Selected row: faint primary tint + left-edge accent so the
        // current selection is unmistakable in the preview pane context.
        bgcolor: selected
          ? 'primary.50'
          : isDragging
          ? 'action.selected'
          : 'transparent',
        boxShadow: selected ? 'inset 3px 0 0 0' : 'none',
        color: selected ? 'primary.main' : undefined,
        transform: CSS.Transform.toString(transform),
        transition,
        '&:hover': {
          // Very pastel sky-blue tint — softer than MUI's action.hover so
          // the row hover doesn't compete with the selected-row treatment.
          bgcolor: selected ? 'primary.50' : PASTEL_HOVER,
          '& .row-actions': { opacity: 1 },
          '& .drag-handle': { opacity: 1 },
        },
      }}
      onClick={() => onSelect(lesson)}
    >
      <Box
        className="drag-handle"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        sx={{
          opacity: 0.3,
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>

      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1,
          bgcolor: `${meta.color}15`,
          color: meta.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {meta.icon}
      </Box>

      <Typography
        variant="body2"
        sx={{ flex: 1, fontWeight: 500, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}
        noWrap
      >
        {/* Gate marker — sits before the title so it scans as a status
            badge (like a checkpoint flag) rather than an action. */}
        {gated && (
          <Tooltip title="Locked until previous lessons are complete">
            <LockIcon fontSize="small" sx={{ color: '#b45309', flexShrink: 0 }} />
          </Tooltip>
        )}
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {lesson.title || 'Untitled lesson'}
        </Box>
      </Typography>

      <Chip
        label={meta.label}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.7rem',
          fontWeight: 600,
          bgcolor: `${meta.color}15`,
          color: meta.color,
          '& .MuiChip-label': { px: 1 },
        }}
      />

      <Box
        className="row-actions"
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: 'flex',
          gap: 0.5,
          // Gate toggle stays visible when active so the gate's existence
          // is always obvious; other actions still hide on no-hover.
          opacity: gated ? 1 : 0,
          transition: 'opacity 150ms ease',
        }}
      >
        <Tooltip
          title={
            gated
              ? 'Gated — learners must complete every lesson before this one. Click to remove.'
              : 'Lock this lesson — learners must complete every previous lesson before reaching it.'
          }
        >
          <IconButton
            size="small"
            onClick={toggleGate}
            sx={{
              color: gated ? '#b45309' : 'text.secondary',
              bgcolor: gated ? '#fff7ed' : 'transparent',
              '&:hover': {
                // Hover stays in the same pastel family — only a barely-
                // perceptible warm shift so the button feels responsive
                // without the previous saturated amber pop.
                color: gated ? '#92400e' : 'primary.main',
                bgcolor: gated ? '#fdf0db' : 'rgba(0, 0, 0, 0.025)',
              },
            }}
          >
            {gated ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit lesson">
          <IconButton size="small" onClick={() => onEdit(lesson)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete lesson">
          <IconButton
            size="small"
            onClick={() => onDelete(lesson.id)}
            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ── Module card ──────────────────────────────────────────────────────────
function SortableModuleCard({
  module,
  lessonsInModule,
  selectedLessonId,
  onSelectLesson,
  onEditModule,
  onDeleteModule,
  onAddLessonForModule,
  onEditLesson,
  onDeleteLesson,
  onToggleGate,
  onLessonReorderInModule,
}: {
  module: any;
  lessonsInModule: any[];
  selectedLessonId: number | null | undefined;
  onSelectLesson: (l: any) => void;
  onEditModule: (m: any) => void;
  onDeleteModule: (id: number) => void;
  onAddLessonForModule: (moduleId: number) => void;
  onEditLesson: (l: any) => void;
  onDeleteLesson: (id: number) => void;
  onToggleGate: (l: any) => void;
  onLessonReorderInModule: (moduleId: number, lessonIds: number[]) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `module-${module.id}`,
  });

  // Local sensors so lesson DnD inside this card doesn't conflict with
  // the module-level DnD context. (PointerSensor with a small activation
  // distance keeps clicks responsive.)
  const lessonSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleLessonDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = lessonsInModule.map((l) => l.id);
    const oldIdx = ids.indexOf(Number(String(active.id).replace('lesson-', '')));
    const newIdx = ids.indexOf(Number(String(over.id).replace('lesson-', '')));
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(ids, oldIdx, newIdx);
    onLessonReorderInModule(module.id, reordered);
  };

  return (
    <Card
      ref={setNodeRef}
      sx={{
        opacity: isDragging ? 0.7 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        overflow: 'visible',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: isDragging ? 4 : 0,
        '&:hover .module-actions': { opacity: 1 },
        '&:hover .module-drag-handle': { opacity: 1 },
      }}
    >
      {/* Module header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          bgcolor: 'grey.50',
          borderBottom: lessonsInModule.length > 0 ? '1px solid' : 'none',
          borderColor: 'divider',
        }}
      >
        <Box
          className="module-drag-handle"
          {...attributes}
          {...listeners}
          sx={{
            opacity: 0.4,
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {module.title || 'Untitled module'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {lessonsInModule.length} {lessonsInModule.length === 1 ? 'lesson' : 'lessons'}
          </Typography>
        </Box>

        <Box className="module-actions" sx={{ display: 'flex', gap: 0.5, opacity: 0, transition: 'opacity 150ms ease' }}>
          <Tooltip title="Edit module">
            <IconButton size="small" onClick={() => onEditModule(module)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete module">
            <IconButton
              size="small"
              onClick={() => onDeleteModule(module.id)}
              sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Lessons */}
      <Box sx={{ px: 1, py: 0.5 }}>
        <DndContext sensors={lessonSensors} collisionDetection={closestCenter} onDragEnd={handleLessonDragEnd}>
          <SortableContext items={lessonsInModule.map((l) => `lesson-${l.id}`)} strategy={verticalListSortingStrategy}>
            {lessonsInModule.map((l) => (
              <SortableLessonRow
                key={l.id}
                lesson={l}
                selected={selectedLessonId === l.id}
                onSelect={onSelectLesson}
                onEdit={onEditLesson}
                onDelete={onDeleteLesson}
                onToggleGate={onToggleGate}
              />
            ))}
          </SortableContext>
        </DndContext>

        {lessonsInModule.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, py: 1 }}>
            No lessons in this module yet.
          </Typography>
        )}

        {/* Per-module add — uses the same branded cyan gradient as the
            top-level "Create" CTA on Manage Content, so admins see a
            consistent primary brand action across the surface. */}
        <Box sx={{ px: 1, py: 1.5, mt: 0.5 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAddLessonForModule(module.id)}
          >
            Add lesson
          </Button>
        </Box>
      </Box>
    </Card>
  );
}

// ── Top-level builder ────────────────────────────────────────────────────
export default function AdminCurriculumBuilder({
  modules,
  lessons,
  selectedLessonId,
  onSelectLesson,
  onAddModule,
  onEditModule,
  onDeleteModule,
  onAddLessonForModule,
  onEditLesson,
  onDeleteLesson,
  onToggleGate,
  onReorderModules,
  onReorderLessons,
}: AdminCurriculumBuilderProps) {
  // Default no-op so rows can call without nullcheck noise.
  const selectLesson = onSelectLesson ?? (() => {});
  // Group lessons by module id, preserving the lesson list's natural order
  // for each group (the hook returns them in the order the server provided).
  const lessonsByModule = useMemo(() => {
    const map = new Map<number | null, any[]>();
    for (const l of lessons) {
      const key = l.moduleId ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(l);
    }
    return map;
  }, [lessons]);

  const orphanLessons = lessonsByModule.get(null) ?? [];

  const moduleSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleModuleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = modules.map((m) => m.id);
    const oldIdx = ids.indexOf(Number(String(active.id).replace('module-', '')));
    const newIdx = ids.indexOf(Number(String(over.id).replace('module-', '')));
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(ids, oldIdx, newIdx);
    onReorderModules(reordered);
  };

  // For lesson reordering within a module, we still need to send the FULL
  // course's lesson order (the API works on the flat per-course list). So
  // we splice the reordered module's lesson ids into the existing flat
  // order and call the global reorder handler.
  const handleLessonReorderInModule = (moduleId: number, newLessonIdsInModule: number[]) => {
    const fullOrder: number[] = [];
    for (const l of lessons) {
      if (l.moduleId === moduleId) continue; // skip — we'll splice these in
      fullOrder.push(l.id);
    }
    // Re-inject the reordered group at the index of the first lesson that
    // originally belonged to this module.
    const firstIdx = lessons.findIndex((l) => l.moduleId === moduleId);
    if (firstIdx === -1) {
      onReorderLessons([...fullOrder, ...newLessonIdsInModule]);
      return;
    }
    // Map original full-list index to a position in the new fullOrder array.
    // Lessons before firstIdx that don't belong to this module map 1:1 by
    // count; we use that count as the splice insertion point.
    const insertAt = lessons.slice(0, firstIdx).filter((l) => l.moduleId !== moduleId).length;
    const next = [...fullOrder.slice(0, insertAt), ...newLessonIdsInModule, ...fullOrder.slice(insertAt)];
    onReorderLessons(next);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <DndContext sensors={moduleSensors} collisionDetection={closestCenter} onDragEnd={handleModuleDragEnd}>
        <SortableContext items={modules.map((m) => `module-${m.id}`)} strategy={verticalListSortingStrategy}>
          {modules.map((m) => (
            <SortableModuleCard
              key={m.id}
              module={m}
              lessonsInModule={lessonsByModule.get(m.id) ?? []}
              selectedLessonId={selectedLessonId}
              onSelectLesson={selectLesson}
              onEditModule={onEditModule}
              onDeleteModule={onDeleteModule}
              onAddLessonForModule={onAddLessonForModule}
              onEditLesson={onEditLesson}
              onDeleteLesson={onDeleteLesson}
              onToggleGate={onToggleGate}
              onLessonReorderInModule={handleLessonReorderInModule}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* Orphan lessons (no module) — only shown if they exist. */}
      {orphanLessons.length > 0 && (
        <Card sx={{ border: '1px dashed', borderColor: 'divider', boxShadow: 0 }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
              Lessons without a module
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {orphanLessons.length} {orphanLessons.length === 1 ? 'lesson' : 'lessons'} · drop these into a module by editing each one
            </Typography>
          </Box>
          <Box sx={{ px: 1, py: 0.5 }}>
            {orphanLessons.map((l) => (
              <SortableLessonRow
                key={l.id}
                lesson={l}
                selected={selectedLessonId === l.id}
                onSelect={selectLesson}
                onEdit={onEditLesson}
                onDelete={onDeleteLesson}
                onToggleGate={onToggleGate}
              />
            ))}
          </Box>
        </Card>
      )}

      {modules.length === 0 && orphanLessons.length === 0 ? (
        // Empty state — central CTA. Same primary action as the bottom
        // bar; matches Thinkific's "build your curriculum" feel.
        <Box
          sx={{
            p: 6,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Build your curriculum
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Add a module to group related lessons, then drop lessons inside.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={onAddModule}
          >
            Add module
          </Button>
        </Box>
      ) : (
        // "Add module" sits BELOW the last module card. Centered with a
        // dashed-border affordance so it reads as the next-step CTA after
        // all the existing modules.
        <Box
          onClick={onAddModule}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 2,
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            cursor: 'pointer',
            color: 'primary.main',
            fontWeight: 600,
            transition: 'background-color 150ms ease, border-color 150ms ease',
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'primary.main',
            },
          }}
        >
          <AddIcon fontSize="small" />
          <Typography variant="body2" fontWeight={700}>
            Add module
          </Typography>
        </Box>
      )}
    </Box>
  );
}
